import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  BookOpen,
  Users,
  Globe,
  Lock,
  AlertCircle,
  CheckCircle,
  Upload,
  Image as ImageIcon,
} from "lucide-react";
import {
  getAvailableFields,
  getFieldSubFields,
  getPhotoURL,
} from "../../utils/fieldUtils";
import {
  sanitizeInput,
  logSecurityEvent,
  rateLimiter,
  handleSecureError,
} from "../../utils/securityUtils";
import "./CreateCommunityRoomModal.css";

const CreateCommunityRoomModal = ({
  communityId,
  community,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    field: typeof community?.field === "string" ? community.field : "",
    subField: typeof community?.subField === "string" ? community.subField : "",
    photoURL: null,
  });

  const [subFields, setSubFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Memoize fields data to prevent infinite re-renders
  const fields = useMemo(() => getAvailableFields(), []);
  const allSubFields = useMemo(() => getFieldSubFields(), []);

  useEffect(() => {
    // Auto-select community's field if available
    if (
      community?.field &&
      typeof community.field === "string" &&
      fields.includes(community.field)
    ) {
      setFormData((prev) => ({ ...prev, field: community.field }));
    }
  }, [community?.field, fields]);

  useEffect(() => {
    if (formData.field && allSubFields[formData.field]) {
      setSubFields(allSubFields[formData.field] || []);

      // Auto-select subfield if it matches community's subfield
      if (
        community?.subField &&
        typeof community.subField === "string" &&
        allSubFields[formData.field]?.includes(community.subField)
      ) {
        setFormData((prev) => ({ ...prev, subField: community.subField }));
      } else {
        setFormData((prev) => ({ ...prev, subField: "" }));
      }
    } else {
      setSubFields([]);
      setFormData((prev) => ({ ...prev, subField: "" }));
    }
  }, [formData.field, allSubFields, community?.subField]);

  // 🔒 SECURE FORM VALIDATION
  const validateForm = () => {
    const newErrors = {};

    // 🔒 Secure name validation (15+ chars as per backend guide)
    const name = sanitizeInput(formData.name.trim());
    if (!name) {
      newErrors.name = "Room name is required";
    } else if (name.length < 15) {
      newErrors.name = "Room name must be at least 15 characters long";
    } else if (name.length > 100) {
      newErrors.name = "Room name must be less than 100 characters";
    } else if (!/^[a-zA-Z0-9\s\-_.,!?()&]+$/.test(name)) {
      newErrors.name = "Room name contains invalid characters";
    }

    // 🔒 Secure description validation (30+ chars as per backend guide)
    const description = sanitizeInput(formData.description.trim());
    if (!description) {
      newErrors.description = "Room description is required";
    } else if (description.length < 30) {
      newErrors.description =
        "Room description must be at least 30 characters long";
    } else if (description.length > 500) {
      newErrors.description =
        "Room description must be less than 500 characters";
    }

    // Field validation
    if (!formData.field) {
      newErrors.field = "Research field is required";
    }

    if (!formData.subField) {
      newErrors.subField = "Research sub-field is required";
    }

    // 🔒 Log validation failures for security monitoring
    if (Object.keys(newErrors).length > 0) {
      logSecurityEvent("form_validation_failed", {
        form: "create_room",
        errors: Object.keys(newErrors),
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔒 SECURE FORM SUBMISSION
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🔒 Rate limiting check
    if (!rateLimiter.isAllowed("create_room", 3, 600000)) {
      // 3 per 10 minutes
      setErrors({
        submit:
          "Too many room creation attempts. Please wait before trying again.",
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Set photoURL based on field selection using getPhotoURL utility
      const photoURL = getPhotoURL(formData.field);

      // 🔒 Sanitize room data before submission
      const roomData = {
        ...formData,
        photoURL,
        name: sanitizeInput(formData.name.trim()),
        description: sanitizeInput(formData.description.trim()),
        field: sanitizeInput(formData.field),
        subField: sanitizeInput(formData.subField),
      };

      // 🔒 Log room creation attempt
      logSecurityEvent("room_creation_attempt", {
        nameLength: roomData.name.length,
        descriptionLength: roomData.description.length,
        field: roomData.field,
        subField: roomData.subField,
      });

      await onSuccess(roomData);

      // 🔒 Log successful room creation
      logSecurityEvent("room_created_successfully", {
        roomName: roomData.name,
        field: roomData.field,
      });
    } catch (error) {
      // 🔒 Secure error handling
      const secureErrorMessage = handleSecureError(error);

      // 🔒 Log room creation failure
      logSecurityEvent("room_creation_failed", {
        error: secureErrorMessage,
      });

      // Handle specific backend errors
      if (error.message?.includes("limit")) {
        setErrors({
          submit: "Maximum room limit reached (5 rooms per moderator)",
        });
      } else if (error.message?.includes("moderator")) {
        setErrors({ submit: "Only moderators can create rooms" });
      } else {
        setErrors({
          submit: secureErrorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear specific field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }

    // Clear submit error when any field changes
    if (errors.submit) {
      setErrors((prev) => ({ ...prev, submit: null }));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-room-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <BookOpen size={20} />
            <h2>Create Research Room</h2>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="community-context">
            <p>
              Creating a research room in <strong>{community?.name}</strong>
            </p>
            <span className="community-field">
              {community?.field} → {community?.subField}
            </span>
          </div>

          <form onSubmit={handleSubmit} className="create-room-form">
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="roomName">
                  Room Name <span className="required">*</span>
                </label>
                <input
                  id="roomName"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Deep Learning Paper Reviews & Discussions"
                  className={errors.name ? "error" : ""}
                  maxLength={100}
                />
                <div className="input-footer">
                  {errors.name && (
                    <span className="error-message">
                      <AlertCircle size={14} />
                      {errors.name}
                    </span>
                  )}
                  <span className="char-count">
                    {formData.name.length}/100 characters
                    {formData.name.length >= 15 && (
                      <CheckCircle size={14} className="valid-icon" />
                    )}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="roomDescription">
                  Description <span className="required">*</span>
                </label>
                <textarea
                  id="roomDescription"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Describe the purpose and focus of this research room. What topics will be discussed? What kind of research will be conducted?"
                  className={errors.description ? "error" : ""}
                  rows={4}
                  maxLength={500}
                />
                <div className="input-footer">
                  {errors.description && (
                    <span className="error-message">
                      <AlertCircle size={14} />
                      {errors.description}
                    </span>
                  )}
                  <span className="char-count">
                    {formData.description.length}/500 characters
                    {formData.description.length >= 30 && (
                      <CheckCircle size={14} className="valid-icon" />
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Research Focus</h3>

              <div className="fields-row">
                <div className="form-group">
                  <label htmlFor="field">
                    Field <span className="required">*</span>
                  </label>
                  <select
                    id="field"
                    value={formData.field || ""}
                    onChange={(e) => handleInputChange("field", e.target.value)}
                    className={errors.field ? "error" : ""}
                  >
                    <option value="">Select research field</option>
                    {fields.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                  {errors.field && (
                    <span className="error-message">
                      <AlertCircle size={14} />
                      {errors.field}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="subField">
                    Sub-field <span className="required">*</span>
                  </label>
                  <select
                    id="subField"
                    value={formData.subField || ""}
                    onChange={(e) =>
                      handleInputChange("subField", e.target.value)
                    }
                    className={errors.subField ? "error" : ""}
                    disabled={!formData.field}
                  >
                    <option value="">Select sub-field</option>
                    {subFields.map((subField) => (
                      <option key={subField} value={subField}>
                        {subField}
                      </option>
                    ))}
                  </select>
                  {errors.subField && (
                    <span className="error-message">
                      <AlertCircle size={14} />
                      {errors.subField}
                    </span>
                  )}
                </div>
              </div>

              {formData.field && (
                <div className="field-preview">
                  <ImageIcon size={16} />
                  <span>Room will use {formData.field} field image</span>
                </div>
              )}
            </div>

            <div className="room-settings-info">
              <div className="setting-item">
                <Users size={16} />
                <div>
                  <strong>Member Limit:</strong> Maximum 8 members
                </div>
              </div>
              <div className="setting-item">
                <Globe size={16} />
                <div>
                  <strong>Visibility:</strong> Visible to all community members
                </div>
              </div>
              <div className="setting-item">
                <Lock size={16} />
                <div>
                  <strong>Joining:</strong> Requires approval from room leader
                </div>
              </div>
            </div>

            {errors.submit && (
              <div className="submit-error">
                <AlertCircle size={16} />
                {errors.submit}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="create-btn"
                disabled={
                  loading ||
                  !formData.name.trim() ||
                  !formData.description.trim() ||
                  !formData.field ||
                  !formData.subField
                }
              >
                {loading ? (
                  <>
                    <div className="spinner" />
                    Creating Room...
                  </>
                ) : (
                  <>
                    <BookOpen size={16} />
                    Create Research Room
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateCommunityRoomModal;
